import React, { useState } from 'react';
import { motion } from 'framer-motion';

const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would normally send the data to your backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div className="relative overflow-hidden" style={{
      background: 'linear-gradient(to bottom, #141019, #1A1625)',
    }}>
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 blur-[150px] rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500/10 blur-[150px] rounded-full"></div>

      <div className="container mx-auto py-12 px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold mb-8 text-white"
          >
            Contact Us
          </motion.h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={sectionVariants}
                className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg p-8 mb-6 border border-white/5"
              >
                <h2 className="text-2xl font-semibold mb-4 text-white">Get In Touch</h2>
                <p className="text-white/80 mb-6">
                  Have questions about our products or services? We're here to help.
                  Fill out the form and our team will get back to you as soon as possible.
                </p>

                <div className="mb-4">
                  <h3 className="font-semibold text-white mb-2">Our Office</h3>
                  <p className="text-white/70">123 Developer Lane</p>
                  <p className="text-white/70">Tech City, TC 10101</p>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold text-white mb-2">Email</h3>
                  <p className="text-white/70">info@devmarket.com</p>
                </div>

                <div>
                  <h3 className="font-semibold text-white mb-2">Phone</h3>
                  <p className="text-white/70">+1 (555) 123-4567</p>
                </div>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={sectionVariants}
                className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg p-8 border border-white/5"
              >
                <h2 className="text-2xl font-semibold mb-4 text-white">Business Hours</h2>
                <div className="grid grid-cols-2 gap-2 text-white/70">
                  <div>Monday - Friday:</div>
                  <div>9:00 AM - 6:00 PM</div>
                  <div>Saturday:</div>
                  <div>10:00 AM - 4:00 PM</div>
                  <div>Sunday:</div>
                  <div>Closed</div>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={sectionVariants}
              className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg p-8 border border-white/5"
            >
              {submitted ? (
                <div className="text-center py-8">
                  <div className="text-[#F670C7] text-5xl mb-4">✓</div>
                  <h2 className="text-2xl font-semibold mb-2 text-white">Thank You!</h2>
                  <p className="text-white/80 mb-4">Your message has been received.</p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSubmitted(false)}
                    className="bg-gradient-to-r from-[#7F56D9] to-[#F670C7] text-white px-6 py-2 rounded-md hover:opacity-90 transition duration-300"
                  >
                    Send Another Message
                  </motion.button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold mb-6 text-white">Send Us a Message</h2>
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label htmlFor="name" className="block text-white/80 mb-2">Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-[#2A1E44]/50 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F670C7] text-white"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="email" className="block text-white/80 mb-2">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-[#2A1E44]/50 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F670C7] text-white"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="subject" className="block text-white/80 mb-2">Subject</label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-[#2A1E44]/50 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F670C7] text-white"
                        required
                      >
                        <option value="">Select a subject</option>
                        <option value="general">General Inquiry</option>
                        <option value="support">Technical Support</option>
                        <option value="sales">Sales</option>
                        <option value="partnership">Partnership</option>
                      </select>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="message" className="block text-white/80 mb-2">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className="w-full px-4 py-2 bg-[#2A1E44]/50 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F670C7] text-white"
                        required
                      ></textarea>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#7F56D9] to-[#F670C7] text-white px-6 py-3 rounded-md hover:opacity-90 transition duration-300"
                    >
                      Send Message
                    </motion.button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
